import { LOCIZE_NAMESPACES } from '@infield/features/i18n';
import { MissingChecklistsState } from '@infield/providers/checklist-scheduler-provider/types';
import { useContext } from 'react';

import { UseSchedulesPageContext } from './use-schedules-page.context';

export const useSchedulesPage = () => {
  const {
    useTranslation,
    useSchedulesTableSelectedDateFilter,
    useCreatePlannedChecklistsMutation,
    useFetchChecklistsFromSchedulerQuery,
    useSchedulesDataGridStates,
    createChecklistsSequentially,
    showChecklistCreationToast,
  } = useContext(UseSchedulesPageContext);

  const { t } = useTranslation(LOCIZE_NAMESPACES.checklist);

  const { selectedDateFilter } = useSchedulesTableSelectedDateFilter();
  const { createPlannedChecklists } = useCreatePlannedChecklistsMutation();
  const {
    data: checklists,
    isLoading,
    refetch,
  } = useFetchChecklistsFromSchedulerQuery({
    dateRangeFilter: selectedDateFilter,
  });

  const {
    setChecklistCreationState,
    schedulesDataGridSelectedRows: selectedRows,
    setChecklistCreationProgressPercent,
  } = useSchedulesDataGridStates();

  const createChecklistsFromToolbar = async (): Promise<void> => {
    // 1. Get the selected checklists based on the selected rows
    const checklistsToCreate = checklists?.filter((row) =>
      selectedRows.includes(row.externalId)
    );

    if (!checklistsToCreate || checklistsToCreate.length === 0) {
      return;
    }

    // 2. Set the state to indicate that checklists are being created (triggers the loading banner)
    setChecklistCreationState(MissingChecklistsState.CREATING_CHECKLISTS);

    // 3. Create the checklists
    const { createdChecklists, failedChecklists } =
      await createChecklistsSequentially(
        checklistsToCreate,
        createPlannedChecklists,
        setChecklistCreationProgressPercent
      );

    // 4. Refetch so that fresh data is shown in the table
    await refetch();

    // 5. Show relevant toast based on the result
    showChecklistCreationToast({ createdChecklists, failedChecklists }, t);

    // 6. Reset the progress percent to 0
    // TODO(FO-1221): Close the loading banner
    setChecklistCreationProgressPercent(0);

    // 7. Reset the state to indicate that checklists are no longer being created
    setChecklistCreationState(MissingChecklistsState.NONE);
  };

  return {
    checklists,
    isLoadingChecklists: isLoading,
    createChecklistsFromToolbar,
  };
};
